import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { toast } from 'sonner';
import { Loader2, Search, TriangleAlert, Ban, UserCheck, History } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const MembersReviewPanel = ({ language = 'en' }) => {
  const es = language === 'es';
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState(null); // { mode: 'warn'|'suspend'|'history', member }
  const [message, setMessage] = useState('');
  const [duration, setDuration] = useState('7');
  const [history, setHistory] = useState(null);
  const [warnCounts, setWarnCounts] = useState({});

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const [{ data }, counts] = await Promise.all([
        axios.get(`${API}/api/admin/users`, { ...cfg, params: { limit: 50, search: q || undefined } }),
        axios.get(`${API}/api/admin/warnings/counts`, cfg).then((r) => r.data.counts || {}).catch(() => ({})),
      ]);
      setWarnCounts(counts);
      setMembers((data.users || []).filter((u) => u.role !== 'super_admin'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'No se pudieron cargar los miembros' : 'Could not load members'));
      setMembers([]);
    } finally { setLoading(false); }
  }, [es]);

  useEffect(() => { load(); }, [load]);

  const submitAction = async () => {
    if (!dialog) return;
    setBusyId(dialog.member.user_id);
    try {
      if (dialog.mode === 'warn') {
        await axios.post(`${API}/api/admin/users/warn`, { target_user_id: dialog.member.user_id, message }, cfg);
        toast.success(es ? 'Advertencia enviada' : 'Warning sent');
      } else {
        await axios.post(`${API}/api/admin/users/ban`, { target_user_id: dialog.member.user_id, reason: message || 'Policy violation', duration_days: parseInt(duration, 10) }, cfg);
        toast.success(es ? `Suspendido ${duration} días` : `Suspended for ${duration} days`);
      }
      setDialog(null); setMessage(''); setDuration('7');
      load(search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'Error' : 'Something went wrong'));
    } finally { setBusyId(null); }
  };

  const openHistory = async (m) => {
    setDialog({ mode: 'history', member: m });
    setHistory(null);
    try {
      const { data } = await axios.get(`${API}/api/admin/users/${m.user_id}/warnings`, cfg);
      setHistory(data.warnings || []);
    } catch { setHistory([]); }
  };

  const unban = async (m) => {
    setBusyId(m.user_id);
    try {
      await axios.post(`${API}/api/admin/users/${m.user_id}/unban`, {}, cfg);
      toast.success(es ? 'Reactivado' : 'Reinstated');
      load(search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'Error' : 'Something went wrong'));
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-3" data-testid="members-review-panel">
      <form onSubmit={(e) => { e.preventDefault(); load(search); }} className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={es ? 'Buscar por nombre o email…' : 'Search by name or email…'} className="pl-9" data-testid="members-search" />
      </form>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : members.length === 0 ? (
        <p className="text-center py-10 text-muted-foreground">{es ? 'No hay miembros' : 'No members found'}</p>
      ) : members.map((m) => (
        <Card key={m.user_id} data-testid={`member-row-${m.user_id}`}>
          <CardContent className="p-3 flex items-center gap-3">
            <Avatar className="w-9 h-9">
              {m.picture ? <AvatarImage src={m.picture} /> : null}
              <AvatarFallback className="text-xs bg-primary/15">{(m.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate flex items-center gap-2">
                {m.name}
                {warnCounts[m.user_id] > 0 && (
                  <Badge className="text-[10px] bg-amber-500 hover:bg-amber-500 gap-1" data-testid={`member-warncount-${m.user_id}`}>
                    <TriangleAlert className="w-3 h-3" />{warnCounts[m.user_id]}
                  </Badge>
                )}
                {m.is_banned && <Badge variant="destructive" className="text-[10px]">{es ? 'Suspendido' : 'Suspended'}</Badge>}
                {m.role && m.role !== 'user' && <Badge variant="outline" className="text-[10px] capitalize">{m.role}</Badge>}
              </p>
              <p className="text-xs text-muted-foreground truncate">{m.email}</p>
            </div>
            {m.is_banned ? (
              <Button size="sm" variant="outline" onClick={() => unban(m)} disabled={busyId === m.user_id} data-testid={`member-unban-${m.user_id}`}>
                <UserCheck className="w-4 h-4 mr-1" /> {es ? 'Reactivar' : 'Reinstate'}
              </Button>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={() => openHistory(m)} data-testid={`member-history-${m.user_id}`} title={es ? 'Historial' : 'History'}>
                  <History className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setDialog({ mode: 'warn', member: m }); setMessage(''); }} disabled={busyId === m.user_id} data-testid={`member-warn-${m.user_id}`}>
                  <TriangleAlert className="w-4 h-4 mr-1 text-amber-500" /> {es ? 'Advertir' : 'Warn'}
                </Button>
                <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => { setDialog({ mode: 'suspend', member: m }); setMessage(''); setDuration('7'); }} disabled={busyId === m.user_id} data-testid={`member-suspend-${m.user_id}`}>
                  <Ban className="w-4 h-4 mr-1" /> {es ? 'Suspender' : 'Suspend'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!dialog} onOpenChange={(v) => { if (!v) { setDialog(null); setHistory(null); } }}>
        <DialogContent className="max-w-sm" data-testid="member-action-dialog">
          {dialog?.mode === 'history' ? (
            <>
              <DialogTitle>{es ? 'Historial de advertencias' : 'Warning history'} — {dialog?.member?.name}</DialogTitle>
              <DialogDescription>{es ? 'Advertencias emitidas a este miembro.' : 'Warnings issued to this member.'}</DialogDescription>
              <div className="space-y-2 py-1 max-h-72 overflow-y-auto" data-testid="warning-history-list">
                {history === null ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : history.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">{es ? 'Sin advertencias. ¡Buen historial!' : 'No warnings. Clean record!'}</p>
                ) : history.map((w) => (
                  <div key={w.id} className="rounded-lg border p-2.5" data-testid={`warning-${w.id}`}>
                    <p className="text-sm">{w.message}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {es ? 'por' : 'by'} {w.issued_by_name} · {new Date(w.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setDialog(null); setHistory(null); }}>{es ? 'Cerrar' : 'Close'}</Button>
              </DialogFooter>
            </>
          ) : (
          <>
          <DialogTitle>
            {dialog?.mode === 'warn' ? (es ? 'Advertir miembro' : 'Warn member') : (es ? 'Suspender miembro' : 'Suspend member')} — {dialog?.member?.name}
          </DialogTitle>
          <DialogDescription>
            {dialog?.mode === 'warn'
              ? (es ? 'Envía una nota amable recordando las normas de la comunidad.' : 'Send a friendly note reminding them of community guidelines.')
              : (es ? 'Suspensión temporal. El miembro no podrá iniciar sesión hasta que expire.' : 'A temporary suspension. The member cannot sign in until it expires.')}
          </DialogDescription>
          <div className="space-y-3 py-1">
            {dialog?.mode === 'suspend' && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">{es ? 'Duración' : 'Duration'}</p>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger data-testid="suspend-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{es ? '1 día' : '1 day'}</SelectItem>
                    <SelectItem value="7">{es ? '7 días' : '7 days'}</SelectItem>
                    <SelectItem value="30">{es ? '30 días' : '30 days'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
              placeholder={dialog?.mode === 'warn' ? (es ? 'Mensaje de advertencia…' : 'Warning message…') : (es ? 'Motivo…' : 'Reason…')}
              data-testid="member-action-message" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} data-testid="member-action-cancel">{es ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={submitAction} disabled={busyId} className={dialog?.mode === 'suspend' ? 'bg-rose-600 hover:bg-rose-700' : ''} data-testid="member-action-confirm">
              {busyId ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {dialog?.mode === 'warn' ? (es ? 'Enviar' : 'Send') : (es ? 'Suspender' : 'Suspend')}
            </Button>
          </DialogFooter>
          </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersReviewPanel;
